import { useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  Card,
  IconButton,
  Collapse,
  Checkbox,
  Chip,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Unit, Process, ActionItem } from '@urbanmix-tech/shared-js';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';
import { DATE_PERIOD_CONFIGS, getDatePeriod } from '@/constants/datePeriods';

interface DateHighlightProps {
  units: Unit[];
  processes: Process[];
  actionItems: ActionItem[];
  datePeriodFilter: string[];
  onChangeDatePeriodFilter: (selected: string[]) => void;
}

export type DateRangeType = 'next_7_days' | 'next_week' | 'next_30_days' | 'custom';

interface ScheduledItem {
  id: string;
  type: 'process' | 'task';
  title: string;
  date: Date;
  unitId?: string;
  unitName?: string | null;
  buildingId?: string | number | null;
  projectId?: string | number | null;
  processType?: string;
  processColor?: string;
  status?: string;
  description?: string | null;
}

export const DateHighlight = ({
  units,
  processes,
  actionItems,
  datePeriodFilter,
  onChangeDatePeriodFilter,
}: DateHighlightProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { getProcessTypeConfig } = useProcessSettings();

  // Create a map of unit IDs to unit names
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => {
      map.set(unit.id, unit);
    });
    return map;
  }, [units]);

  // Collect all scheduled items (tasks only, as we're focused on due dates)
  const scheduledItems = useMemo(() => {
    const items: ScheduledItem[] = [];

    // Add action items with due dates (exclude completed tasks)
    actionItems.forEach((item) => {
      if (item.status === 'done') return;

      if (item.dueDate) {
        const date = convertPseudoDateToRealDate(item.dueDate);
        if (date) {
          const unit = item.unitId ? unitMap.get(item.unitId) : undefined;

          items.push({
            id: item.id,
            type: 'task',
            title: item.title || 'Task',
            date,
            unitId: item.unitId ?? undefined,
            unitName: unit?.name ?? undefined,
            buildingId: unit?.buildingId ?? undefined,
            projectId: item.projectId ?? undefined,
            status: item.status,
            description: item.description ?? undefined,
          });
        }
      }
    });

    // Sort by date
    items.sort((a, b) => a.date.getTime() - b.date.getTime());

    return items;
  }, [actionItems, unitMap]);

  // Calculate statistics by period
  const periodStats = useMemo(() => {
    const stats = new Map<string, number>();

    // Initialize all periods with 0
    DATE_PERIOD_CONFIGS.forEach((period) => {
      stats.set(period.value, 0);
    });

    // Count tasks in each period
    scheduledItems.forEach((item) => {
      const period = getDatePeriod(item.date);
      stats.set(period, (stats.get(period) || 0) + 1);
    });

    // Count unscheduled tasks (tasks without due dates)
    const unscheduledCount = actionItems.filter(
      (item) => item.status !== 'done' && !item.dueDate
    ).length;
    stats.set('unscheduled', unscheduledCount);

    return Array.from(stats.entries()).map(([period, count]) => {
      const config = DATE_PERIOD_CONFIGS.find((p) => p.value === period)!;
      return {
        period,
        label: config.label,
        color: config.color,
        count,
      };
    });
  }, [scheduledItems, actionItems]);

  // Overall statistics
  const stats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const totalTasks = scheduledItems.length;
    const overdueTasks = scheduledItems.filter(
      (item) => item.date.getTime() < now.getTime()
    ).length;
    const onTimeTasks = totalTasks - overdueTasks;
    const uniqueUnits = new Set(scheduledItems.filter((item) => item.unitId).map((item) => item.unitId)).size;
    const uniqueBuildings = new Set(scheduledItems.filter((item) => item.buildingId).map((item) => item.buildingId)).size;

    // Count unscheduled tasks
    const unscheduledTasks = actionItems.filter(
      (item) => item.status !== 'done' && !item.dueDate
    ).length;

    return {
      totalTasks,
      onTimeTasks,
      overdueTasks,
      unscheduledTasks,
      uniqueUnits,
      uniqueBuildings,
    };
  }, [scheduledItems, actionItems]);

  const handleRemoveFilter = (period: string) => {
    onChangeDatePeriodFilter(datePeriodFilter.filter((p) => p !== period));
  };

  const handleClearAll = () => {
    onChangeDatePeriodFilter([]);
  };

  const hasActiveFilters = datePeriodFilter.length > 0;

  return (
    <Stack gap="8px">
      {/* Active Filters Chips */}
      {/* {hasActiveFilters && (
        <Stack direction="row" gap="8px" alignItems="center" flexWrap="wrap">
          <Typography variant="caption" className="text-stone-300">
            Active Filters:
          </Typography>
          {datePeriodFilter.map((period) => {
            const config = DATE_PERIOD_CONFIGS.find((p) => p.value === period);
            return (
              <Chip
                key={period}
                label={config?.label || period}
                onDelete={() => handleRemoveFilter(period)}
                size="small"
                sx={{
                  height: '24px',
                  backgroundColor: config?.color,
                  color: 'white',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            );
          })}
          <Chip
            label="Clear All"
            onClick={handleClearAll}
            size="small"
            variant="outlined"
            sx={{
              height: '24px',
              color: 'rgba(100, 116, 139, 1)',
              borderColor: 'rgba(100, 116, 139, 0.5)',
              '&:hover': {
                borderColor: 'rgba(100, 116, 139, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          />
        </Stack>
      )} */}

      {/* Collapsible Stats Section */}
      <Stack
        className="rounded-lg border border-stone-200/20 bg-zinc-900/30"
        sx={{ overflow: 'hidden' }}
      >
        {/* Collapse Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className="cursor-pointer px-12 py-8"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            },
          }}
        >
          <Typography variant="body2" className="text-stone-300">
            {isExpanded ? 'Hide' : 'Show'} Schedule Stats
          </Typography>
          <IconButton
            size="small"
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              color: 'rgba(255, 255, 255, 0.5)',
              padding: '4px',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Stats Panels */}
        <Collapse in={isExpanded}>
          <Stack direction="row" gap="12px" className="p-12">
            {/* Panel 1: Date Periods (Legend & Filter) - Spans 2 columns */}
            <Stack
              gap="6px"
              className="w-2/3 min-w-[520px] rounded-[10px] border border-white/5 bg-zinc-900 px-16 py-12"
            >
              <Typography variant="h6" className="pb-4 text-stone-200">
                Date Periods
              </Typography>

              {/* Two column grid for date periods */}
              <Stack direction="row" gap="12px">
                {/* Left column */}
                <Stack gap="4px" className="flex-1">
                  {periodStats.slice(0, Math.ceil(periodStats.length / 2)).map((stat) => (
                    <Stack
                      key={stat.period}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" alignItems="center" gap="8px" className="flex-1">
                        <Box
                          className="rounded"
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: stat.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" className="text-stone-200">
                          {stat.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap="12px">
                        <Typography
                          variant="caption2"
                          className="min-w-[60px] text-stone-200/80"
                        >
                          {`${stat.count} tasks`}
                        </Typography>
                        <Checkbox
                          checked={datePeriodFilter.includes(stat.period)}
                          onChange={(_e, checked) => {
                            if (checked) {
                              onChangeDatePeriodFilter([
                                ...datePeriodFilter.filter((p) => p !== stat.period),
                                stat.period,
                              ]);
                            } else {
                              onChangeDatePeriodFilter([
                                ...datePeriodFilter.filter((p) => p !== stat.period),
                              ]);
                            }
                          }}
                          size="small"
                          className="p-0"
                          sx={{ '& .MuiSvgIcon-root': { color: 'white' } }}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>

                {/* Right column */}
                <Stack gap="4px" className="flex-1">
                  {periodStats.slice(Math.ceil(periodStats.length / 2)).map((stat) => (
                    <Stack
                      key={stat.period}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" alignItems="center" gap="8px" className="flex-1">
                        <Box
                          className="rounded"
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: stat.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" className="text-stone-200">
                          {stat.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap="12px">
                        <Typography
                          variant="caption2"
                          className="min-w-[60px] text-stone-200/80"
                        >
                          {`${stat.count} tasks`}
                        </Typography>
                        <Checkbox
                          checked={datePeriodFilter.includes(stat.period)}
                          onChange={(_e, checked) => {
                            if (checked) {
                              onChangeDatePeriodFilter([
                                ...datePeriodFilter.filter((p) => p !== stat.period),
                                stat.period,
                              ]);
                            } else {
                              onChangeDatePeriodFilter([
                                ...datePeriodFilter.filter((p) => p !== stat.period),
                              ]);
                            }
                          }}
                          size="small"
                          className="p-0"
                          sx={{ '& .MuiSvgIcon-root': { color: 'white' } }}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>

            {/* Panel 2: Task Status Overview - Now 1/3 width */}
            <Stack
              gap="6px"
              className="w-1/3 min-w-[260px] rounded-[10px] border border-white/5 bg-zinc-900 px-16 py-12"
            >
              <Typography variant="h6" className="pb-4 text-stone-200">
                Task Status
              </Typography>

              <Stack gap="6px">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" className="text-stone-200">
                    Total Tasks
                  </Typography>
                  <Typography variant="h5" className="text-white">
                    {stats.totalTasks}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" className="text-stone-200">
                    On Time
                  </Typography>
                  <Typography variant="h5" className="text-green-500">
                    {stats.onTimeTasks}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" className="text-stone-200">
                    Overdue
                  </Typography>
                  <Typography variant="h5" className="text-red-500">
                    {stats.overdueTasks}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" className="text-stone-200">
                    Unscheduled
                  </Typography>
                  <Typography variant="h5" className="text-stone-300">
                    {stats.unscheduledTasks}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
};
